import { create } from "zustand";

import type {
  AppointmentRequest,
  AppointmentRequestWithoutRegistration,
  AppointmentResponse,
} from "src/core/backend/clients/backendClient";
import { backendService } from "src/core/backend/services/backendService";
import { dateService } from "../../../core/services/dateService";
import { logService } from "../../../core/services/logService";
import type { ClinicModel } from "../../clinics/models/clinicModel";
import type { DoctorProfileModel } from "../../clinics/models/doctorProfileModel";
import type { ParticipantModel } from "../../participant/models/participantModel";
import type { AppointmentModel } from "../models/appointmentModel";
import { appointmentService } from "./appointmentService";

interface AppointmentState {
  readonly loading: boolean;
  readonly appointments: AppointmentModel[];
  readonly appointmentFilterType: string;
  readonly filteredAppointments: AppointmentModel[];

  readonly selectedPerson?: ParticipantModel;
  setSelectedPerson: (person?: ParticipantModel) => void;

  readonly selectedClinic?: ClinicModel;
  setSelectedClinic: (clinic?: ClinicModel) => void;

  readonly selectedDoctorProfile?: DoctorProfileModel;
  setSelectedDoctorProfile: (doctorProfile?: DoctorProfileModel) => void;

  readonly selectedDateTime?: Date;
  setSelectedDateTime: (dateTime?: Date) => void;

  readonly quickAppointment: boolean;
  setQuickAppointment: (quickAppointment: boolean) => void;

  readonly withoutRegLogin: boolean;
  setWithoutRegLogin: (withoutRegLogin: boolean) => void;

  readonly lastAppointmentRequest?: AppointmentRequest;
  readonly lastAppointmentRequestWithoutRegistration?: AppointmentRequestWithoutRegistration;

  clear: () => void;

  appointmentList: () => Promise<void>;
  appointmentPost: (
    request: AppointmentRequest
  ) => Promise<AppointmentResponse>;
  appointmentCreateWithoutRegistration: (
    request: AppointmentRequestWithoutRegistration
  ) => Promise<AppointmentResponse>;
  appointmentDelete: (id: number) => Promise<void>;
  appointmentConfirm: (id: number, key: string) => Promise<void>;
  appointmentTerminate: (id: number, key: string) => Promise<void>;
}

export const useAppointmentStore = create<AppointmentState>()((set) => ({
  loading: false,

  appointments: [],
  appointmentFilterType: "",
  filteredAppointments: [],

  setSelectedPerson: (person) => set({ selectedPerson: person }),
  setSelectedClinic: (clinic) => set({ selectedClinic: clinic }),
  setSelectedDoctorProfile: (doctorProfile) =>
    set({ selectedDoctorProfile: doctorProfile }),
  setSelectedDateTime: (dateTime) => set({ selectedDateTime: dateTime }),

  quickAppointment: false,
  setQuickAppointment: (quickAppointment) => set({ quickAppointment }),

  withoutRegLogin: false,
  setWithoutRegLogin: (withoutRegLogin) => set({ withoutRegLogin }),

  clear: () => {
    set({
      loading: false,
      appointments: [],
      appointmentFilterType: "",
      filteredAppointments: [],
      selectedPerson: undefined,
      selectedClinic: undefined,
      selectedDoctorProfile: undefined,
      selectedDateTime: undefined,
      quickAppointment: false,
      withoutRegLogin: false,
      lastAppointmentRequest: undefined,
      lastAppointmentRequestWithoutRegistration: undefined,
    });
  },

  appointmentList: async () => {
    set({ loading: true });

    const response = await appointmentService
      .appointmentList()
      .then((response) => {
        if (logService.globalLog) logService.info("appointmentList", response);
        return response;
      })
      .catch((error) => {
        logService.error("appointmentList", error);
        set({ loading: false });
        throw error;
      });

    const appointments = response.map((item) => {
      const appointment: AppointmentModel = {
        ...item,
        severity: "info",
        isDeletable: false,
        isLoading: false,
      };
      dateService.datesFromUTCString(appointment);
      return appointment;
    });

    set({ appointments: appointments, loading: false });
  },

  appointmentPost: async (request: AppointmentRequest) => {
    set({ loading: true, lastAppointmentRequest: request });
    return await appointmentService
      .appointmentPost(request)
      .then((response) => {
        set({ loading: false });
        if (logService.globalLog) logService.info("appointmentPost", response);
        return response;
      })
      .catch((error) => {
        logService.error("appointmentPost", error);
        set({ loading: false });
        throw error;
      });
  },

  appointmentCreateWithoutRegistration: async (
    request: AppointmentRequestWithoutRegistration
  ) => {
    set({ loading: true });

    backendService.nullToUndefined(request);
    backendService.personRequestFix(request);

    set({ lastAppointmentRequestWithoutRegistration: request });

    return await appointmentService
      .appointmentCreateWithoutRegistration(request)
      .then((response) => {
        set({ loading: false });
        if (logService.globalLog)
          logService.info("appointmentCreateWithoutRegistration", response);
        return response;
      })
      .catch((error) => {
        logService.error("appointmentCreateWithoutRegistration", error);
        set({ loading: false });
        throw error;
      });
  },

  appointmentDelete: async (id: number) => {
    set({ loading: true });

    await appointmentService
      .appointmentDelete(id)
      .then(() => {
        if (logService.globalLog) logService.info("appointmentDelete", id);
      })
      .catch((error) => {
        logService.error("appointmentDelete", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },

  appointmentConfirm: async (id: number, key: string) => {
    set({ loading: true });

    await appointmentService
      .appointmentConfirm(id, key)
      .then(() => {
        if (logService.globalLog) logService.info("appointmentConfirm", id);
      })
      .catch((error) => {
        logService.error("appointmentConfirm", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },

  appointmentTerminate: async (id: number, key: string) => {
    set({ loading: true });

    await appointmentService
      .appointmentTerminate(id, key)
      .then(() => {
        if (logService.globalLog) logService.info("appointmentTerminate", id);
      })
      .catch((error) => {
        logService.error("appointmentTerminate", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },
}));
